import React, { useCallback, useEffect, useRef, useState } from "react";
import { NumberInput, TextInput, useInput, useRedirect, useRefresh, useUpdate } from "react-admin";
import AsyncSelect from "react-select/async";
import axios from 'axios';
import { apiUrl } from "../../dataProvider";
import { FormHelperText, Button, Box, Typography, Button as MUIButton } from '@material-ui/core';
import { SaveButton, Toolbar, useNotify, useCreate } from "react-admin";
import { useForm, useFormState } from 'react-final-form';
import Grid from "@material-ui/core/Grid";
import './style.css';
import { CUSTOM_RESOURCES } from "./type";
import { validateRequired } from "../../utils/Validations";


export const CustomSelect = ({
    label,
    isEdit,
    isMulti = false,
    optionValue = 'sku_code',
    optionLabel = 'name',
    validate = [],
    query = {},
    pageSize = 1000,
    paramId,
    idsource = '',
    sideSource = '',
    sideValueKey = 'sku_code',
    clearSourcesOnChange = [],
    isDisabled = false,
    source,
    resource,
    defaultValue = null,
    baseUrl,
    transformOptions,
    skipPaging = false,
    requireQueryKeys = [],
    placeholder,
    menuPlacement = "auto",
    closeMenuOnSelect = true,
    ...props
}) => {
    const [list, setList] = useState(
        {
            data: [],
            raw: [],
        }
    );
    const [searchValue, setSearchValue] = useState('');
    const [loading, setLoading] = useState(false);
    const listRawRef = useRef([]);
    const form = useForm();

    const { input, isRequired,
        meta: { touched, error, }
    } = useInput({ source, validate, value: defaultValue })

    const { input: inputSourceId } = useInput({ source: idsource || 'test' })

    const getOptionText = (item) =>
        String(item?.[optionLabel] ?? item?.name ?? item?.[optionValue] ?? item?.id ?? '');

    const getOptionId = (item) =>
        item?.[optionValue] ?? item?.id;

    const getLists = (params) => {
        const apiBase = baseUrl || apiUrl;
        const url = paramId ? `${apiBase}/${resource}/${paramId}` : `${apiBase}/${resource}`;
        return axios.get(url, { params }).then(({ data }) => {
            const payload = data?.data ?? data;
            if (typeof transformOptions === 'function') {
                return transformOptions(payload);
            }
            return Array.isArray(payload) ? payload : [];
        })
    }

    const filterOptions = (value, options = listRawRef.current) => {
        const search = String(value || '').toLowerCase().trim();
        if (!search) return options;

        return options.filter((item) => {
            const text = getOptionText(item).toLowerCase();
            const id = String(getOptionId(item) ?? '').toLowerCase();
            return text.includes(search) || id.includes(search);
        });
    }

    const getSearchOptions = (value, callback) => {
        callback(filterOptions(value, listRawRef.current));
    };

    const setStoreList = useCallback(async (params) => {
        try {
            setLoading(true);
            const response = await getLists(params);
            listRawRef.current = response;
            setList({ data: response, raw: response });
        } catch ({ response }) {
            listRawRef.current = [];
            setList({ data: [], raw: [] });

            const status = response?.status;
            if (status === 401 || status === 403) {
                window.location.replace('/#/login');
            }
        } finally {
            setLoading(false);
        }

    }, [paramId, baseUrl, resource, transformOptions]) // eslint-disable-line react-hooks/exhaustive-deps

    const hasRequiredQuery =
        !requireQueryKeys.length ||
        requireQueryKeys.every(
            (key) => query?.[key] != null && query[key] !== ""
        );

    const queryKey = JSON.stringify(query);

    /** Initial load; skips fetch until required query keys are present */
    useEffect(() => {
        if (!hasRequiredQuery) {
            listRawRef.current = [];
            setList({ data: [], raw: [] });
            return;
        }

        setStoreList(
            skipPaging
                ? { ...query }
                : {
                    'page[number]': 1,
                    'page[size]': pageSize,
                    ...query
                }
        );
    }, [hasRequiredQuery, queryKey, setStoreList, paramId]) // eslint-disable-line react-hooks/exhaustive-deps


    const handleChange = callback => selectedopt => {
        if (idsource) {
            inputSourceId.onChange(selectedopt?.name)
        }
        if (sideSource) {
            form.change(
                sideSource,
                selectedopt == null
                    ? null
                    : (isMulti
                        ? (Array.isArray(selectedopt) ? selectedopt.map((item) => item?.[sideValueKey]).filter(Boolean) : [])
                        : (selectedopt?.[sideValueKey] ?? null))
            );
        }
        if (Array.isArray(clearSourcesOnChange) && clearSourcesOnChange.length > 0) {
            clearSourcesOnChange.forEach((clearSource) => {
                form.change(clearSource, []);
            });
        }
        if (isMulti) {
            callback(
                Array.isArray(selectedopt)
                    ? selectedopt.map((item) => getOptionId(item))
                    : []
            );
        } else {
            callback(selectedopt?.[optionValue]);
        }
        setSearchValue('');
    }

    // Always resolve selection from the full option list so search filtering
    // never drops already-selected multi values.
    // Prefer optionValue (usually sku_code) over id — size names like "3"/"10"
    // collide with numeric ids and otherwise show the wrong option.
    const findSelectedOption = (value) => {
        if (value == null || value === "") return undefined;
        if (typeof value === "object" && value.feature_group != null) {
            return list.raw.find(
                (item) => String(item?.[optionValue] ?? "") === String(value.feature_group)
            );
        }
        const needle = String(value);
        return (
            list.raw.find((item) => String(getOptionId(item) ?? "") === needle) ||
            list.raw.find((item) => String(item?.name ?? "") === needle)
        );
    };

    const selectedValue = isMulti
        ? list.raw.filter((item) => {
            const selected = Array.isArray(input.value) ? input.value : [];
            return selected
                .map((entry) => String(entry))
                .includes(String(getOptionId(item) ?? ''));
        })
        : findSelectedOption(input.value);

    // Keep side field (e.g. school_sku_code) in sync when selection is resolved from loaded options
    useEffect(() => {
        if (!sideSource || isMulti) return;
        const nextSideValue = selectedValue?.[sideValueKey] ?? null;
        const currentSideValue = form.getState().values?.[sideSource];
        if (currentSideValue !== nextSideValue) {
            form.change(sideSource, nextSideValue);
        }
    }, [selectedValue, sideSource, sideValueKey, isMulti, form]);

    return (
        <div style={{ marginBottom: '4px', width: '100%' }}>
            {label ? <p className="label">{label} {isRequired ? '*' : ''}</p> : null}
            <AsyncSelect
                name={input.name}
                onBlur={input.onBlur}
                isClearable
                styles={{
                    container: provided => ({ ...provided, width: '100%' }),
                    menu: provided => ({ ...provided, zIndex: 9999 }),
                    menuPortal: provided => ({ ...provided, zIndex: 9999 }),
                }}
                isMulti={isMulti}
                closeMenuOnSelect={closeMenuOnSelect}
                blurInputOnSelect={closeMenuOnSelect}
                hideSelectedOptions={closeMenuOnSelect}
                isLoading={loading}
                inputValue={searchValue}
                defaultOptions={list.raw}
                isDisabled={isDisabled}
                placeholder={placeholder}
                getOptionLabel={getOptionText}
                getOptionValue={(e) => String(getOptionId(e) ?? '')}
                loadOptions={getSearchOptions}
                value={selectedValue || (isMulti ? [] : null)}
                onChange={handleChange(input.onChange)}
                onInputChange={(value, meta) => {
                    if (meta.action === 'input-change') {
                        setSearchValue(value);
                        return value;
                    }
                    if (meta.action === 'menu-close' || meta.action === 'set-value') {
                        setSearchValue('');
                        return '';
                    }
                    return searchValue;
                }}
                filterOption={null}
                menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                menuPlacement={menuPlacement}
                maxMenuHeight={280}
            />
            {touched && error ? <FormHelperText error>{error === 'ra.validation.required' ? "Required" : error}</FormHelperText> : null}
        </div>
    )
}


const getOutsideFeature = (data) => {
    const list = data?.map(item => {
        if(item.feature_group === 'N') {
            return null;
        } else if (item.feature_group === 'G' || item.feature_group === 'CG') {
            return {
                feature_group: item.feature_group,
                inlay_material: item.inlay_material,
                inlay_width: item.inlay_width,
                inlay_finish: item.inlay_finish,
                mga: item.mga,
                groove_group: item.groove_group,
                inlay_qty: item.inlay_qty,
                inlay_thickness: item.inlay_thickness,
                inlay_volume: item.inlay_volume,
            }
        } else if (item.feature_group === 'M') {
            return {
                feature_group: item.feature_group,
                machine_pattern: item.machine_pattern,
                cerakote_color: item.cerakote_color,
                ring_material: item.ring_material,
            }
        } else if (item.feature_group === 'A') {
            return {
                feature_group: item.feature_group,
                cerakote: item.cerakote,
            }
        } else if (item.feature_group === 'D') {
            return {
                feature_group: item.feature_group,
                gemstone_arrangement: item.gemstone_arrangement,
                gemstone_material: item.gemstone_material,
                quantity: item.quantity,
                markup: item.markup,
                gemstone_cut: item.gemstone_cut,
                gemstone_setting: item.gemstone_setting,
                gemstone_size: item.gemstone_size,
            }
        }
        return null;
    });

    const filteredList = list.filter(item => item !== null);
    return filteredList;
}

const getEngraving = (data) => {
    if(data?.type === 'NONE') {
        return null;
    } else if(data?.type === 'D') {
        return {
            type: data.type,
            design_pattern: data.design_pattern,
            ring_material: data.ring_material,
        }
    }
    return null;
}


const CustomSave = ({ handleSubmitWithRedirect, resource, setDatasource, datasource
    , ...props }) => {
    const notify = useNotify();
    const form = useForm();
    const { hasValidationErrors } = useFormState();
    const refresh = useRefresh();
    const redirect = useRedirect();


    const [create] = useCreate(resource);
    const [update]  = useUpdate(resource, props.record.id);

    const handleClick = useCallback(async (type = 'price') => {

        try {
            const formValues = form.getState().values;
            let values = {...formValues};
            values.outside_feature = getOutsideFeature(values?.outside_feature || []);
            values.engraving = getEngraving(values.engraving);
            if(!values.ring_finish?.[0]) values.ring_finish = [];

            if (values.non_ring) {
                values = {
                    id: formValues.id,
                    title: formValues.title,
                    description: formValues.description,
                    ring_material: formValues.ring_material,
                    notes: formValues.notes,
                    additional_price: formValues.additional_price,
                    non_ring: !!formValues.non_ring
                }
            }
            console.log(values);
            setDatasource((prev) => ({ ...prev, loading: true }))

            if (type === 'save' || type === 'save as') {
                const callback = (values.id && type === 'save') ? update : create;
                callback(
                    {
                        payload: {
                            data: {
                                data: values,
                                price: datasource.data,
                                final_price: datasource.data.price
                            }
                        },
                    },
                    {
                        onSuccess: ({ data }) => {
                            notify(data.message, { type: 'success' });
                            setDatasource((prev) => ({ ...prev, loading: false, data: null }))
                            redirect(`/${resource}`);
                        },
                        onFailure: ({ response: { data: { errors } } }) => {
                            if ('message' in errors) {
                                notify(errors.message, { type: 'warning' });
                                return;
                            }
                            setDatasource((prev) => ({ ...prev, loading: false }))
                            const message = errors[Object.keys(errors)[0]][0];
                            notify(message, { type: 'warning' });
                        },
                    }
                );
            } else {
                const res = await axios.post(`${apiUrl}/getPrice`, values)
                setDatasource((prev) => ({ ...prev, loading: false, data: res.data }))

                notify("Price request Successfull", "success", 2000);
            }

        } catch (errors) {
            setDatasource((prev) => ({ ...prev, loading: false }))
            notify("Combination not found.", "error", 2000);
        }

    }, [form, datasource]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <Box>
            <Button style={{
                marginLeft: 10,
                marginRight: 10,
            }}
                variant='text'
                disabled={!form.getState().dirty}
                onClick={() => {
                    refresh()
                    setDatasource((prev) => ({
                        ...prev, data: null, featureGroupCount: 1,
                        additionalPrice: 1,
                    }))

                }}>
                Reset
            </Button>
            <SaveButton  {...props} handleSubmitWithRedirect={handleClick} label="Price" disabled={hasValidationErrors} />
            <SaveButton {...props} handleSubmitWithRedirect={() => handleClick('save')} label="Save" disabled={hasValidationErrors || datasource.data === null} />
            {form.getState().values.id && <SaveButton {...props} handleSubmitWithRedirect={() => handleClick('save as')} label="Save As" disabled={hasValidationErrors || datasource.data === null} />}
        </Box>)
};

export const Save = props => {
    return (
        <Toolbar {...props}>
            <CustomSave
                redirect="list"
                submitOnEnter={false}
                variant="text"
                {...props}
            />
        </Toolbar>
    );
}

export const InlayType = ({ type, disabled = false, val = null, count, ...props }) => {

    const source = `outside_feature.${count}`

    const { input } = useInput({ source: `${source}.feature_group` })

    useEffect(() => {
        if (!val) {
            input.onChange('N')
        }
    }, [val]) // eslint-disable-line react-hooks/exhaustive-deps

    console.log('>>>', input.value);
    return (
        < >
            <Grid item xs={12} style={{ paddingTop: 15 }}>
                <CustomSelect label="" isDisabled={disabled} idsource={`${source}.feature_group`} source={`${source}.feature_group`} validate={[validateRequired]} resource={CUSTOM_RESOURCES["custom-pricing/feature-groups"]} />
            </Grid>

            {(input.value === 'G' || input.value === 'CG') &&
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    flexWrap: 'wrap',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    width: '100%',
                    marginTop: 10
                }}   >
                    <Grid item xs={5}>
                        <CustomSelect label="Inlay Material" isDisabled={disabled} source={`${source}.inlay_material`} validate={[validateRequired]} resource={CUSTOM_RESOURCES["custom-pricing/inlays"]} />
                    </Grid>
                    <Grid item xs={5}>
                        <NumberInput disabled={disabled} source={`${source}.inlay_width`} defaultValue={null} validate={[validateRequired]} label="Inlay Width" fullWidth />
                    </Grid>
                    <Grid item xs={5}>
                        <CustomSelect label="Inlay Finish" isDisabled={disabled} source={`${source}.inlay_finish`} resource={CUSTOM_RESOURCES["custom-pricing/finishes"]} />
                    </Grid>
                    <Grid item xs={5}>
                        <CustomSelect label="Mga Color Option" isDisabled={disabled} source={`${source}.mga`} resource={CUSTOM_RESOURCES['custom-pricing/mga-color-options']} />
                    </Grid>
                    <Grid item xs={5}>
                        <CustomSelect label="Groove Group" isDisabled={disabled} source={`${source}.groove_group`} resource={CUSTOM_RESOURCES['custom-pricing/groove-groups']} />
                    </Grid>
                    <Grid item xs={5}>
                        <TextInput disabled={disabled} source={`${source}.inlay_qty`} defaultValue={null} validate={[validateRequired]} label="Inlay Quantity" fullWidth />
                    </Grid>
                    <Grid item xs={5}>
                        <TextInput source={`${source}.inlay_thickness`} defaultValue={null} disabled={disabled} label="Inlay Thickness" fullWidth />
                    </Grid>
                    <Grid item xs={5}>
                        <TextInput source={`${source}.inlay_volume`} defaultValue={null} disabled={disabled} label="Inlay Volume" fullWidth />
                    </Grid>
                </Box>
            }
            {input.value === 'M' &&
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    flexWrap: 'wrap',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    width: '100%',
                    marginTop: 10
                }}   >
                    <Grid item xs={5}>
                        <CustomSelect label="Machine Pattern" isDisabled={disabled} source={`${source}.machine_pattern`} validate={[validateRequired]} resource={CUSTOM_RESOURCES["custom-pricing/machine-patterns"]} />
                    </Grid>

                    <Grid item xs={5}>
                        <CustomSelect label="Cerakote Color" isDisabled={disabled} source={`${source}.cerakote_color`} validate={[validateRequired]} resource={CUSTOM_RESOURCES["custom-pricing/machine-pattern-color-options"]} />
                    </Grid>
                    <Grid item xs={5}>
                        <CustomSelect label="Ring Material" isDisabled={disabled} source={`${source}.ring_material`} validate={[validateRequired]} resource={CUSTOM_RESOURCES["custom-pricing/ring-materials"]} />
                    </Grid>
                </Box>
            }

            {input.value === 'A' &&
                <>
                    <Grid item xs={5}>
                        <CustomSelect label="All Cerakote Option" isDisabled={disabled} source={`${source}.cerakote`} validate={[validateRequired]} resource={CUSTOM_RESOURCES["custom-pricing/all-cerakote-options"]} />
                    </Grid>
                </>
            }

            {input.value === "D" &&
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    flexWrap: 'wrap',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    width: '100%',
                    marginTop: 10
                }}   >
                    <Grid item xs={5}>
                        <CustomSelect label="Gemstone Arrangement" isDisabled={disabled} source={`${source}.gemstone_arrangement`} resource={CUSTOM_RESOURCES["custom-pricing/gemstone-arrangements"]} />
                    </Grid>

                    <Grid item xs={5}>
                        <p className="label"></p>
                        <CustomSelect label="Gemstone Material" isDisabled={disabled} source={`${source}.gemstone_material`} validate={[validateRequired]} resource={CUSTOM_RESOURCES["custom-pricing/gemstones"]} />
                    </Grid>

                    <Grid item xs={5}>
                        <CustomSelect label="Gemstone Cut" isDisabled={disabled} source={`${source}.gemstone_cut`} validate={[validateRequired]} resource={CUSTOM_RESOURCES["custom-pricing/gemstone-cuts"]} />
                    </Grid>

                    <Grid item xs={5}>
                        <CustomSelect label="Gemstone Settings" isDisabled={disabled} source={`${source}.gemstone_setting`} resource={CUSTOM_RESOURCES["custom-pricing/gemstone-settings"]} />
                    </Grid>

                    <Grid item xs={5}>
                        <CustomSelect label="Gemstone Size" isDisabled={disabled} source={`${source}.gemstone_size`} validate={[validateRequired]} resource={CUSTOM_RESOURCES["custom-pricing/gemstone-dimensions"]} />
                    </Grid>

                    <Grid item xs={5}>
                        <TextInput disabled={disabled} source={`${source}.quantity`} defaultValue={null} label="Quantity" fullWidth />
                    </Grid>

                    <Grid item xs={5}>
                        <TextInput disabled={disabled} source={`${source}.markup`} defaultValue={null} label="Markup" fullWidth />
                    </Grid>
                </Box>
            }
        </>)

}

export const InlayTypeShow = ({ type, disabled = false, val = null, count, ...props }) => {
    const source = `outside_feature.${count}`
    return (
        < >
            <Grid item xs={12} style={{ paddingTop: 15 }}>
                <CustomSelect label="" isDisabled={disabled} source={`${source}.feature_group`} validate={[validateRequired]} resource={CUSTOM_RESOURCES["custom-pricing/feature-groups"]} />
            </Grid>

            {val.feature_group === 'G' &&
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    flexWrap: 'wrap',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    width: '100%',
                    marginTop: 10
                }}   >
                    <Grid item xs={5}>
                        <CustomSelect label="Width" isDisabled={disabled} source={`${source}.inlay_width`} validate={[validateRequired]} resource={CUSTOM_RESOURCES["custom-pricing/widths"]} />
                    </Grid>
                    <Grid item xs={5}>
                        <TextInput disabled={disabled} source={`${source}.inlay_qty`} defaultValue={null} validate={[validateRequired]} label="Inlay Quantity" fullWidth />

                    </Grid>

                    <Grid item xs={5}>
                        <p className="label"></p>
                        <CustomSelect label="Inlay Material" isDisabled={disabled} source={`${source}.inlay_material`} validate={[validateRequired]} resource={CUSTOM_RESOURCES["custom-pricing/inlays"]} />
                    </Grid>

                    <Grid item xs={5}>
                        <p className="label"></p>
                        <CustomSelect label="Mga Color Option" isDisabled={disabled} source={`${source}.mga`} resource={CUSTOM_RESOURCES['custom-pricing/mga-color-options']} />
                    </Grid>
                    <Grid item xs={5}>
                        <CustomSelect label="Groove Group" isDisabled={disabled} source={`${source}.groove_group`} resource={CUSTOM_RESOURCES['custom-pricing/groove-groups']} />
                    </Grid>
                    <Grid item xs={5}>
                        <TextInput source={`${source}_inlay_thickness`} defaultValue={null} disabled={disabled} label="Inlay Thickness" fullWidth />
                    </Grid>
                    <Grid item xs={5}>
                        <TextInput source={`${source}_inlay_volume`} defaultValue={null} disabled={disabled} label="Inlay Volume" fullWidth />
                    </Grid>

                </Box>
            }
            {val.feature_group === 'M' &&
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    flexWrap: 'wrap',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    width: '100%',
                    marginTop: 10
                }}   >
                    <Grid item xs={5}>
                        <p className="label"></p>
                        <CustomSelect label="Machine Pattern" isDisabled={disabled} source={`${source}.machine_pattern`} validate={[validateRequired]} resource={CUSTOM_RESOURCES["custom-pricing/machine-patterns"]} />
                    </Grid>

                    <Grid item xs={5}>
                        <CustomSelect label="Cerakote Color" isDisabled={disabled} source={`${source}.cerakote_color`} validate={[validateRequired]} resource={CUSTOM_RESOURCES["custom-pricing/machine-pattern-color-options"]} />
                    </Grid>
                    <Grid item xs={5}>
                        <CustomSelect label="Ring Material" isDisabled={disabled} source={`${source}.ring_material`} validate={[validateRequired]} resource={CUSTOM_RESOURCES["custom-pricing/ring-materials"]} />
                    </Grid>
                </Box>
            }

            {val.feature_group === "A" &&
                <>
                    <Grid item xs={5}>
                        <CustomSelect label="All Cerakote Option" isDisabled={disabled} source={`${source}.cerakote`} validate={[validateRequired]} resource={CUSTOM_RESOURCES["custom-pricing/all-cerakote-options"]} />
                    </Grid>
                </>
            }

            {val.feature_group === "D" &&
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    flexWrap: 'wrap',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    width: '100%',
                    marginTop: 10
                }}   >
                    <Grid item xs={5}>
                        <p className="label"></p>
                        <CustomSelect label="Gemstone Arrangement" isDisabled={disabled} source={`${source}.gemstone_arrangement`} resource={CUSTOM_RESOURCES["custom-pricing/gemstone-arrangements"]} />
                    </Grid>
                    <Grid item xs={5}>
                        <CustomSelect label="Gemstone Material" isDisabled={disabled} source={`${source}.gemstone_material`} validate={[validateRequired]} resource={CUSTOM_RESOURCES["custom-pricing/gemstones"]} />
                    </Grid>
                    <Grid item xs={5}>
                        <CustomSelect label="Gemstone Cut" isDisabled={disabled} source={`${source}.gemstone_cut`} validate={[validateRequired]} resource={CUSTOM_RESOURCES["custom-pricing/gemstone-cuts"]} />
                    </Grid>
                    <Grid item xs={5}>
                        <CustomSelect label="Gemstone Settings" isDisabled={disabled} source={`${source}_gemstone-settings`} resource={CUSTOM_RESOURCES["custom-pricing/gemstone-settings"]} />
                    </Grid>
                    <Grid item xs={5}>
                        <CustomSelect label="Gemstone Size" isDisabled={disabled} source={`${source}_gemstone-dimensions`} validate={[validateRequired]} resource={CUSTOM_RESOURCES["custom-pricing/gemstone-dimensions"]} />
                    </Grid>
                    <Grid item xs={5}>
                        <TextInput disabled={disabled} source={`${source}.quantity`} defaultValue={null} label="Quantity" fullWidth />
                    </Grid>
                    <Grid item xs={5}>
                        <TextInput disabled={disabled} source={`${source}.markup`} defaultValue={null} label="Markup" fullWidth />
                    </Grid>
                </Box>
            }
        </>)

}


export const EngravingType = ({ disabled = false, value = null, ...props }) => {

    const source = 'engraving';
    const { input } = useInput({ source: `${source}.type` })

    useEffect(() => {
        if (!value) {
            input.onChange('NONE')
        } else {
            input.onChange('D')
        }
    }, [value]) // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <>
            <Grid item xs={12}>
                <CustomSelect label="Engraving Option" isDisabled={disabled} idsource={`${source}.type`} source={`${source}.type`} resource={CUSTOM_RESOURCES["custom-pricing/engraving-options"]} />
            </Grid>

            {input.value === 'D' ?
                <>
                    <Grid item xs={5}>
                        <CustomSelect label="Machine Pattern" isDisabled={disabled} source={`${source}.design_pattern`} validate={[validateRequired]} resource={CUSTOM_RESOURCES["custom-pricing/machine-patterns"]} />
                    </Grid>
                    <Grid item xs={5}>
                        <CustomSelect label="Ring Material" isDisabled={disabled} source={`${source}.ring_material`} validate={[validateRequired]} resource={CUSTOM_RESOURCES["custom-pricing/ring-materials"]} />
                    </Grid>
                </>
            : null}
        </>)
}

export const EngravingTypeShow = ({ disabled = false, val, ...props }) => {

    const source = `engraving`
    return (
        <>
            <Grid item xs={12}>
                <p className="label">Engraving Option</p>
            </Grid>

            <Grid item xs={5}>
                <CustomSelect label="Machine Pattern" isDisabled={disabled} source={`${source}.design_pattern`} validate={[validateRequired]} resource={CUSTOM_RESOURCES["custom-pricing/machine-patterns"]} />
            </Grid>
            <Grid item xs={5}>
                <CustomSelect label="Ring Material" isDisabled={disabled} source={`${source}.ring_material`} validate={[validateRequired]} resource={CUSTOM_RESOURCES["custom-pricing/ring-materials"]} />
            </Grid>
        </>)
}

export const PERMISSION_LIST_SIGNET = {
    delete_data: 'delete_data',
    edit_data: 'edit_data',
    view_data: 'view_data',
    add_data: 'add_data',
}

export const RingsResources = 'nc-rings';

export const AdditionalPriceButton = ({ setDatasource, datasource, disabled =false }) => {
    const form = useForm();


    return (<Box sx={{ display: 'flex', gap: 10, alignItems: 'center' }}>
        <MUIButton
            variant="contained"
            disabled={disabled}
            onClick={() => {
                if (datasource.additionalPrice > 0) {

                    const values = form.getState().values;

                    const i =datasource.additionalPrice - 1

                    const fields = values
                    delete fields[`${i}@additionalPrice_price`]
                    delete fields[`${i}@additionalPrice_label`]

                    form.reset(fields)


                    setDatasource({ ...datasource, additionalPrice: datasource.additionalPrice - 1 })
                }
            }}
        >-</MUIButton>
        <Typography>{datasource.additionalPrice}</Typography>
        <MUIButton
            disabled={disabled}
            variant="contained"
            onClick={() => {
                setDatasource({ ...datasource, additionalPrice: datasource.additionalPrice + 1 })
            }}
        >+</MUIButton>  </Box>)
}